import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Shared visual renderer — used by the public route, the editor preview (authed) and
// desktop/mobile preview modes. All content is plain text (markup stripped server-side).
export function LandingPageView({ page, mediaSrc, compact = false, formSlot, bookingSlot }) {
  const s = page.sections || {};
  const t = page.theme || {};
  return (
    <div style={{ background: t.bg || "#fff" }} className={compact ? "text-left" : "min-h-screen text-left"} data-testid="lp-view">
      <header className="px-6 py-5" style={{ borderBottom: `1px solid ${t.primary}22` }}>
        <p className="text-sm font-extrabold tracking-tight" style={{ color: t.primary }}>{page.title}</p>
      </header>
      <section className={`mx-auto max-w-3xl px-6 ${compact ? "py-6" : "py-14"}`}>
        <h1 className={`${compact ? "text-2xl" : "text-4xl"} font-extrabold leading-tight`} style={{ color: t.primary }} data-testid="lp-headline">
          {s.headline}
        </h1>
        {s.subheadline && <p className={`${compact ? "text-sm" : "text-lg"} mt-3 text-neutral-600`}>{s.subheadline}</p>}
        {mediaSrc && <img src={mediaSrc} alt={page.title} className="mt-6 w-full rounded-2xl object-cover" style={{ maxHeight: compact ? 140 : 340 }} />}
        <a href="#lp-act" className="mt-6 inline-block rounded-full px-6 py-3 text-sm font-bold text-white" style={{ background: t.accent || t.primary }} data-testid="lp-cta">
          {s.cta_text || "Get in touch"}
        </a>
      </section>
      {(s.benefits || []).length > 0 && (
        <section className="mx-auto max-w-3xl px-6 pb-6">
          <div className="grid gap-2.5 sm:grid-cols-2">
            {s.benefits.map((b, i) => (
              <div key={i} className="rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-sm text-neutral-700">{b}</div>
            ))}
          </div>
        </section>
      )}
      {(s.services || []).length > 0 && (
        <section className="mx-auto max-w-3xl px-6 pb-6">
          <h2 className="mb-3 text-lg font-bold" style={{ color: t.primary }}>Services</h2>
          <div className="space-y-2">
            {s.services.map((sv, i) => (
              <div key={i} className="rounded-xl border border-neutral-200 bg-white/70 px-4 py-3">
                <p className="text-sm font-bold text-neutral-800">{sv.name}</p>
                {sv.description && <p className="text-xs text-neutral-500">{sv.description}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
      {s.about && <section className="mx-auto max-w-3xl px-6 pb-6"><p className="text-sm leading-relaxed text-neutral-600">{s.about}</p></section>}
      {(s.faqs || []).length > 0 && (
        <section className="mx-auto max-w-3xl px-6 pb-8">
          <h2 className="mb-3 text-lg font-bold" style={{ color: t.primary }}>FAQs</h2>
          <div className="space-y-2">
            {s.faqs.map((f, i) => (
              <details key={i} className="rounded-xl border border-neutral-200 bg-white/70 px-4 py-3">
                <summary className="cursor-pointer text-sm font-semibold text-neutral-800">{f.q}</summary>
                <p className="mt-1.5 text-xs text-neutral-600">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
      <section id="lp-act" className="mx-auto max-w-3xl px-6 pb-12">
        {page.page_type === "booking" && bookingSlot ? bookingSlot : formSlot}
      </section>
      <footer className="px-6 py-6 text-center text-[11px] text-neutral-400">Powered by Savy Kerno</footer>
    </div>
  );
}

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", message: "", consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [services, setServices] = useState(null);
  const [svc, setSvc] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    // UTM params ride along on the page fetch — the server records the genuine view.
    api.get(`/lp/public/${slug}${window.location.search || ""}`)
      .then((r) => {
        setPage(r.data);
        if (r.data.page_type === "booking") {
          api.get(`/lp/public/${slug}/services`).then((s2) => setServices(s2.data.services)).catch(() => setServices([]));
        }
      })
      .catch(() => setPage(null));
  }, [slug]);

  // SEO: reflect the published page in the tab title/description while mounted,
  // then restore whatever the app had before on unmount/route change.
  useEffect(() => {
    if (!page) return;
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content") ?? null;
    document.title = `${page.title || page.sections?.headline || "Welcome"} — Savy Kerno`;
    const desc = (page.sections?.subheadline || page.sections?.headline || page.title || "").slice(0, 160);
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
    return () => {
      document.title = prevTitle;
      const m = document.querySelector('meta[name="description"]');
      if (m) {
        if (prevDesc === null) m.remove();
        else m.setAttribute("content", prevDesc);
      }
    };
  }, [page]);

  if (page === undefined) return <div className="flex min-h-screen items-center justify-center text-sm text-neutral-400">Loading…</div>;
  if (page === null) return <div className="flex min-h-screen items-center justify-center text-sm text-neutral-500" data-testid="lp-not-found">This page isn't available.</div>;

  const mediaSrc = page.sections?.image_media_id ? `${API}/api/lp/public-media/${page.sections.image_media_id}` : null;

  const submitLead = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/lp/public/${slug}/leads${window.location.search || ""}`, form);
      setFormError("");
      setSent(true);
    } catch (err) {
      const msg = formatApiError(err);
      setFormError(msg);  // inline, screen-reader-discoverable
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const loadSlots = async (serviceId, d) => {
    setSvc(serviceId); setDate(d); setSlots(null);
    try {
      const { data } = await api.get(`/lp/public/${slug}/availability`, { params: { service_id: serviceId, date: d } });
      setSlots(data.slots);
    } catch (err) {
      toast.error(formatApiError(err));
      setSlots([]);
    }
  };

  const book = async (start) => {
    if (!form.full_name || !form.phone || !form.consent) {
      const msg = "Enter your name, phone and tick consent first";
      setFormError(msg);
      toast.error(msg);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/lp/public/${slug}/book${window.location.search || ""}`, {
        service_id: svc, start, full_name: form.full_name, phone: form.phone, email: form.email || null,
        consent: form.consent, website: form.website,
      });
      setFormError("");
      setBooked(data.appointment);
    } catch (err) {
      const msg = formatApiError(err);
      setFormError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-neutral-300 px-3.5 py-2 text-sm focus:border-neutral-900 focus:outline-none";

  // Clears the inline error as soon as the visitor corrects the form.
  const upd = (key, value) => {
    setFormError("");
    setForm((f) => ({ ...f, [key]: value }));
  };

  const inlineError = formError ? (
    <p role="alert" aria-live="assertive" data-testid="lp-form-error" className="rounded-xl bg-red-50 px-3.5 py-2 text-xs font-semibold text-red-700">
      {formError}
    </p>
  ) : null;

  const contactFields = (
    <div className="space-y-2.5">
      <input className={inputCls} placeholder="Your name *" value={form.full_name} data-testid="lp-form-name" onChange={(e) => upd("full_name", e.target.value)} required />
      <input className={inputCls} placeholder="Phone *" value={form.phone} data-testid="lp-form-phone" onChange={(e) => upd("phone", e.target.value)} required />
      <input className={inputCls} type="email" placeholder="Email (optional)" value={form.email} data-testid="lp-form-email" onChange={(e) => upd("email", e.target.value)} />
      {/* honeypot — invisible to humans, bots fill it */}
      <input className="hidden" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => upd("website", e.target.value)} aria-hidden="true" />
      <label className="flex items-start gap-2 text-[11px] text-neutral-500">
        <input type="checkbox" className="mt-0.5 accent-neutral-900" checked={form.consent} data-testid="lp-form-consent" onChange={(e) => upd("consent", e.target.checked)} />
        {page.consent_text || "I agree to be contacted about my enquiry."}
      </label>
      {inlineError}
    </div>
  );

  const formSlot = sent ? (
    <div className="rounded-2xl border border-green-300 bg-green-50 p-5 text-sm font-semibold text-green-800" data-testid="lp-form-sent">
      Thank you — your enquiry has been sent. The business will be in touch.
    </div>
  ) : (
    <form onSubmit={submitLead} className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-5" data-testid="lp-lead-form">
      <p className="text-base font-bold text-neutral-900">{page.sections?.cta_text || "Get in touch"}</p>
      {contactFields}
      <textarea className={inputCls} rows={3} placeholder="Your message (optional)" value={form.message} data-testid="lp-form-message" onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
      <button type="submit" disabled={busy} className="w-full rounded-full py-2.5 text-sm font-bold text-white disabled:opacity-60" style={{ background: page.theme?.accent || "#111" }} data-testid="lp-form-submit">
        {busy ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );

  const bookingSlot = booked ? (
    <div className="rounded-2xl border border-green-300 bg-green-50 p-5" data-testid="lp-booked">
      <p className="text-sm font-bold text-green-800">Booked — {booked.service_name} with {booked.staff_name}</p>
      <p className="mt-1 text-xs text-green-700">{new Date(booked.start).toLocaleString("en-GB")} ({booked.timezone})</p>
    </div>
  ) : (
    <div className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-5" data-testid="lp-booking">
      <p className="text-base font-bold text-neutral-900">Book an appointment</p>
      {contactFields}
      <div className="grid gap-2.5 sm:grid-cols-2">
        <select className={inputCls} value={svc} data-testid="lp-book-service" onChange={(e) => { setSlots(null); setSvc(e.target.value); if (date && e.target.value) loadSlots(e.target.value, date); }}>
          <option value="">Choose a service…</option>
          {(services || []).map((s2) => <option key={s2.id} value={s2.id}>{s2.name} · {s2.duration_min} min</option>)}
        </select>
        <input type="date" className={inputCls} value={date} data-testid="lp-book-date" min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => { setDate(e.target.value); if (svc && e.target.value) loadSlots(svc, e.target.value); }} />
      </div>
      {slots && (
        <div className="flex flex-wrap gap-1.5" data-testid="lp-slots">
          {slots.length === 0 ? <p className="text-xs text-neutral-500">No free times that day — try another date.</p> :
            slots.slice(0, 24).map((sl) => (
              <button key={sl.start + sl.staff_id} type="button" disabled={busy} onClick={() => book(sl.start)}
                className="rounded-full border border-neutral-300 px-3 py-1 text-xs font-semibold hover:border-neutral-900" data-testid={`lp-slot-${sl.start.slice(11, 16)}`}>
                {new Date(sl.start).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · {sl.staff_name}
              </button>
            ))}
        </div>
      )}
      <p className="text-[11px] text-neutral-400">Times shown are the business's real availability.</p>
    </div>
  );

  return <LandingPageView page={page} mediaSrc={mediaSrc} formSlot={formSlot} bookingSlot={bookingSlot} />;
}
